/**
 * The line grids of the state report-summary covers, as data: the federal Form 3
 * Detailed Summary Page and the North Carolina CRO-1100.
 *
 * A form's line numbers, its boilerplate labels, which schedule each line
 * summarizes, and which of the form's columns a line actually carries are facts
 * about a paper document — not about a screen. They live here so the web report
 * page and the mobile report panel read ONE grid.
 *
 * That is not housekeeping: transcribed a second time for mobile, the CRO-1100's
 * point-in-time balances picked up an election-cycle column the form leaves
 * blank, and half the Form 3 labels drifted from the ones the web page shows for
 * the same lines. A shared grid makes both mistakes impossible to make twice.
 *
 * The FIGURES stay where they were — each consumer reads them off its own DTO by
 * the `field` name here. Nothing in this file computes anything.
 */
import type {
  FecF3SummaryDto,
  FecF3SummaryLineDto,
  NorthCarolinaCfrsSummaryRow,
} from '../generated/dtos.gen';

// ── Federal Form 3 — the Detailed Summary Page ──────────────────────────────

/** A summary field the form asks for in both columns (This Period + cycle-to-date). */
export type FecF3TwoColumnField = {
  [K in keyof FecF3SummaryDto]: FecF3SummaryDto[K] extends FecF3SummaryLineDto ? K : never;
}[keyof FecF3SummaryDto];

/** A summary field the form asks for in Column A only. */
export type FecF3OneColumnField = 'line8' | 'line9' | 'line10';

interface FecF3LineBase {
  /** Display ref, e.g. "6a", "11a(i)", "10". */
  ref: string;
  /** FEC Form 3 boilerplate label. */
  label: string;
  /** Total / net lines. */
  bold?: boolean;
  /** Sub-lines that roll up into the total beneath them. */
  indent?: boolean;
  /**
   * The key the drill-down uses to find the items that file on this line — the
   * paren-free form of `ref`, so it works as a test hook / aria id ("11a(i)" →
   * "11ai"). Absent on a line with no expandable detail.
   */
  lineKey?: string;
}

export interface FecF3TwoColumnLine extends FecF3LineBase {
  columns: 'two';
  field: FecF3TwoColumnField;
}

export interface FecF3OneColumnLine extends FecF3LineBase {
  columns: 'one';
  field: FecF3OneColumnField;
}

export type FecF3SummaryLine = FecF3TwoColumnLine | FecF3OneColumnLine;

/**
 * The cover block — net contributions / net operating expenditures, then the two
 * debt lines.
 *
 * Line 8 (cash on hand at close) is deliberately absent: it is the same figure as
 * the cash bridge's ending (line 27), and a surface that shows both invites a
 * reader to check whether one filing's two closing-cash figures agree. Every
 * consumer renders exactly one, from its own cash bridge.
 */
export const FEC_F3_COVER_LINES: readonly FecF3SummaryLine[] = [
  { columns: 'two', ref: '6a', label: 'Total contributions (other than loans)', field: 'line6a' },
  { columns: 'two', ref: '6b', label: 'Total contribution refunds', field: 'line6b' },
  { columns: 'two', ref: '6c', label: 'Net contributions', field: 'line6c', bold: true },
  { columns: 'two', ref: '7a', label: 'Total operating expenditures', field: 'line7a' },
  { columns: 'two', ref: '7b', label: 'Total offsets to operating expenditures', field: 'line7b' },
  { columns: 'two', ref: '7c', label: 'Net operating expenditures', field: 'line7c', bold: true },
  { columns: 'one', ref: '9', label: 'Debts and obligations owed TO the committee', field: 'line9' },
  { columns: 'one', ref: '10', label: 'Debts and obligations owed BY the committee', field: 'line10' },
];

export const FEC_F3_RECEIPT_LINES: readonly FecF3SummaryLine[] = [
  { columns: 'two', ref: '11a(i)', label: 'Individuals (itemized)', field: 'line11ai', indent: true, lineKey: '11ai' },
  { columns: 'two', ref: '11a(ii)', label: 'Individuals (unitemized)', field: 'line11aii', indent: true, lineKey: '11aii' },
  { columns: 'two', ref: '11a(iii)', label: 'Total individual contributions', field: 'line11aiii', indent: true },
  { columns: 'two', ref: '11b', label: 'Political party committees', field: 'line11b', lineKey: '11b' },
  { columns: 'two', ref: '11c', label: 'Other political committees (PACs)', field: 'line11c', lineKey: '11c' },
  { columns: 'two', ref: '11d', label: 'The candidate', field: 'line11d', lineKey: '11d' },
  { columns: 'two', ref: '11e', label: 'Total contributions', field: 'line11e', bold: true },
  { columns: 'two', ref: '12', label: 'Transfers from authorized committees', field: 'line12', lineKey: '12' },
  { columns: 'two', ref: '13a', label: 'Loans made or guaranteed by the candidate', field: 'line13a', indent: true, lineKey: '13a' },
  { columns: 'two', ref: '13b', label: 'All other loans', field: 'line13b', indent: true, lineKey: '13b' },
  { columns: 'two', ref: '13c', label: 'Total loans', field: 'line13c' },
  { columns: 'two', ref: '14', label: 'Offsets to operating expenditures', field: 'line14', lineKey: '14' },
  { columns: 'two', ref: '15', label: 'Other receipts', field: 'line15', lineKey: '15' },
  { columns: 'two', ref: '16', label: 'Total receipts', field: 'line16', bold: true },
];

export const FEC_F3_DISBURSEMENT_LINES: readonly FecF3SummaryLine[] = [
  { columns: 'two', ref: '17', label: 'Operating expenditures', field: 'line17', lineKey: '17' },
  { columns: 'two', ref: '18', label: 'Transfers to authorized committees', field: 'line18', lineKey: '18' },
  { columns: 'two', ref: '19a', label: 'Loan repayments: candidate', field: 'line19a', indent: true, lineKey: '19a' },
  { columns: 'two', ref: '19b', label: 'Loan repayments: all other loans', field: 'line19b', indent: true, lineKey: '19b' },
  { columns: 'two', ref: '19c', label: 'Total loan repayments', field: 'line19c' },
  { columns: 'two', ref: '20a', label: 'Refunds to individuals', field: 'line20a', indent: true, lineKey: '20a' },
  { columns: 'two', ref: '20b', label: 'Refunds to party committees', field: 'line20b', indent: true, lineKey: '20b' },
  { columns: 'two', ref: '20c', label: 'Refunds to other political committees', field: 'line20c', indent: true, lineKey: '20c' },
  { columns: 'two', ref: '20d', label: 'Total contribution refunds', field: 'line20d' },
  { columns: 'two', ref: '21', label: 'Other disbursements', field: 'line21', lineKey: '21' },
  { columns: 'two', ref: '22', label: 'Total disbursements', field: 'line22', bold: true },
];

// ── North Carolina — the CRO-1100 Detailed Summary ──────────────────────────

export type NcCfrsLineVariant = 'normal' | 'total' | 'balance';

export interface NcCfrsSummaryLine {
  /**
   * The form's line number. Empty for the one line our ledger surfaces that the
   * paper form folds into the contribution schedules (in-kind received), which is
   * shown so the visible lines foot Total Receipts.
   */
  no: string;
  label: string;
  /** The CRO schedule this line summarizes, printed muted as on the paper form. */
  cro?: string;
  field: keyof NorthCarolinaCfrsSummaryRow;
  variant?: NcCfrsLineVariant;
  /**
   * False for a close-of-period snapshot the form asks for in the reporting-period
   * column ONLY — an election-cycle figure there would be a cumulative total the
   * CRO-1100 never requests. Defaults true.
   */
  ecApplies?: boolean;
  indent?: boolean;
}

export interface NcCfrsSummarySection {
  title: string;
  /** The form's own group sub-headers ("11) Other Receipt Sources"), value-less. */
  groups?: readonly { after: string; label: string }[];
  lines: readonly NcCfrsSummaryLine[];
}

/** Line 4, which the form prints above the grid rather than inside a block. */
export const NC_CFRS_CASH_AT_START: NcCfrsSummaryLine = {
  no: '4',
  label: 'Cash on Hand at Start',
  field: 'beginBalance',
  variant: 'balance',
};

/**
 * Receipts (lines 5–12). In-kind received is inserted before the total so the
 * visible lines foot Total Receipts: the paper form folds it into the
 * individual/party/PAC schedules, our ledger keeps it in its own bucket, and it
 * nets against line 17 so cash is unaffected.
 */
export const NC_CFRS_RECEIPTS: NcCfrsSummarySection = {
  title: 'RECEIPTS',
  groups: [{ after: '10', label: 'Other Receipt Sources' }],
  lines: [
    { no: '5', label: 'Aggregated Contributions from Individuals', cro: 'CRO-1205', field: 'individualAggregateContributions' },
    { no: '6', label: 'Contributions from Individuals', cro: 'CRO-1210', field: 'individualNonAggregateContributions' },
    { no: '7', label: 'Contributions from Political Party Committees', cro: 'CRO-1220', field: 'partyContributions' },
    { no: '8', label: 'Contributions from Other Political Committees', cro: 'CRO-1230', field: 'otherCommitteeContributions' },
    { no: '9', label: 'Loan Proceeds', cro: 'CRO-1410', field: 'loanProceeds' },
    { no: '10', label: 'Refunds/Reimbursements to the Committee', cro: 'CRO-1240', field: 'refundsToCommittee' },
    { no: '11a', label: 'Interest on Bank Accounts', cro: 'CRO-1250', field: 'interestEarned', indent: true },
    { no: '11b', label: 'Contributions from Not-For-Profit Organizations', cro: 'CRO-1250', field: 'notForProfitOrgContributions', indent: true },
    { no: '11c', label: 'Outside Sources of Income', cro: 'CRO-1250', field: 'outsideSourcesOfIncome', indent: true },
    { no: '11d', label: 'Legal Expense Fund — Other Sources', cro: 'CRO-1270', field: 'legalExpenseFundOtherSources', indent: true },
    { no: '11e', label: 'Exempt Purchase Price Sales', cro: 'CRO-1265', field: 'exemptSales', indent: true },
    { no: '', label: 'In-Kind Contributions received (goods & services)', cro: 'CRO-1510', field: 'goodsAndServicesContributions', indent: true },
    { no: '12', label: 'TOTAL RECEIPTS', field: 'totalReceipts', variant: 'total' },
  ],
};

/** Expenditures (lines 13–19), closing on Cash on Hand at End. */
export const NC_CFRS_EXPENDITURES: NcCfrsSummarySection = {
  title: 'EXPENDITURES',
  groups: [{ after: '', label: 'Disbursements' }],
  lines: [
    { no: '13a', label: 'Operating Expenditures', cro: 'CRO-1310', field: 'operatingExpenditures', indent: true },
    { no: '13b', label: 'Contributions to Candidates/Political Committees', cro: 'CRO-1310', field: 'contributionsToOtherCommittees', indent: true },
    { no: '13c', label: 'Coordinated Party Expenditures', cro: 'CRO-1310', field: 'coordinatedPartyExpenditures', indent: true },
    { no: '14', label: 'Aggregated Non-Media Expenditures', cro: 'CRO-1315', field: 'aggregatedNonMediaExpenditures' },
    { no: '15', label: 'Loan Repayments', cro: 'CRO-1420', field: 'loanRepayments' },
    { no: '16', label: 'Refunds/Reimbursements from the Committee', cro: 'CRO-1320', field: 'refundsFromCommittee' },
    { no: '17', label: 'In-Kind Contributions', cro: 'CRO-1510', field: 'inKindContributions' },
    { no: '18', label: 'TOTAL EXPENDITURES', field: 'totalExpenditures', variant: 'total' },
    { no: '19', label: 'Cash on Hand at End', field: 'endBalance', variant: 'balance' },
  ],
};

/** Additional information (lines 20–28). 20–24 are reporting-period only. */
export const NC_CFRS_ADDITIONAL: NcCfrsSummarySection = {
  title: 'ADDITIONAL INFORMATION',
  lines: [
    { no: '20', label: 'Non-Monetary Gifts Given to Other Committees', cro: 'CRO-1330', field: 'nonMonetaryGifts', ecApplies: false },
    { no: '21', label: 'Outstanding Loans (incl. ones from other campaigns)', cro: 'CRO-1430', field: 'outstandingLoans', ecApplies: false },
    { no: '22', label: 'Debts and Obligations owed by the Committee', cro: 'CRO-1610', field: 'debtsOwedByCommittee', ecApplies: false },
    { no: '23', label: 'Debts and Obligations owed to the Committee', cro: 'CRO-1620', field: 'debtsOwedToCommittee', ecApplies: false },
    { no: '24', label: 'Account Transfers Within the Committee', cro: 'CRO-1720', field: 'accountTransfers', ecApplies: false },
    { no: '25', label: 'Administrative Support', cro: 'CRO-1710', field: 'administrativeSupport' },
    { no: '26', label: 'Forgiven Loans', cro: 'CRO-1440', field: 'forgivenLoans' },
    { no: '27', label: '48-Hour Notice Reports Sum', cro: 'CRO-2220', field: 'noticesSum' },
    { no: '28', label: 'Contributions to be Refunded', cro: 'CRO-1215', field: 'contributionsToBeRefunded' },
  ],
};
